import { useState, useMemo } from "react";
import { sqlReadRaw as sqlRead } from "../../../utils/sqlClient";
import { computeLineCost } from "../../../utils/estimatePricing";

// Direct "add an Item or Assembly to the estimate" mechanism — for scope that
// isn't a circuit (switchgear, testing/commissioning, temp power, general
// conditions). Priced the same way as a circuit row (estimatePricing.js);
// rolls into the Settings & Rollup tab grouped by category.

const th = { textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text3)", padding: "6px 8px", borderBottom: "1px solid var(--cs-border)" };
const td = { padding: "4px 8px", borderBottom: "1px solid var(--cs-border)", fontSize: 12 };
const inputSm = { width: "100%", fontSize: 12, padding: "3px 6px", border: "1px solid var(--cs-border)", borderRadius: 4 };
const fmt = (n) => n == null || isNaN(n) ? "—" : n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const emptyDraft = { LineItemID: null, Kind: "assembly", ItemID: null, AssemblyID: null, Description: "", Qty: 1, LaborFactorID: null, Notes: "" };

export default function ElecLineItemsPanel({
  activeRevId, isLocked, payloadOf, lineItems, items, assemblies, categories, laborFactors, formulas, settings, onReload,
}) {
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const itemsById = useMemo(() => new Map(items.map(i => [i.ItemID, i])), [items]);
  const assembliesById = useMemo(() => new Map(assemblies.map(a => [a.AssemblyID, a])), [assemblies]);
  const laborFactorsById = useMemo(() => new Map(laborFactors.map(f => [f.FactorID, f])), [laborFactors]);
  const formulasById = useMemo(() => new Map(formulas.map(f => [f.FormulaID, f])), [formulas]);
  const categoryName = (id) => categories.find(c => c.CategoryID === id)?.Name || "Uncategorized";

  function entityFor(li) {
    return li.Kind === "item" ? itemsById.get(li.ItemID) : assembliesById.get(li.AssemblyID);
  }
  function costFor(li) {
    if (!settings) return null;
    const entity = entityFor(li);
    if (!entity) return null;
    const laborFactor = li.LaborFactorID ? laborFactorsById.get(li.LaborFactorID) : laborFactorsById.get(entity.DefaultLaborFactorID);
    return computeLineCost({ Qty: li.Qty }, entity, laborFactor, settings, formulasById);
  }

  async function save() {
    if (isLocked || !draft) return;
    setSaving(true);
    try {
      const payload = {
        line_item_id: draft.LineItemID, revision_id: activeRevId, kind: draft.Kind,
        item_id: draft.Kind === "item" ? draft.ItemID : null,
        assembly_id: draft.Kind === "assembly" ? draft.AssemblyID : null,
        description: draft.Description || null, qty: +draft.Qty || 1,
        labor_factor_id: draft.LaborFactorID || null, notes: draft.Notes || null,
      };
      await sqlRead(`EXEC sp_SaveEstimateCostLineItem @payload = '${payloadOf(payload)}'`);
      setDraft(null);
      onReload();
    } catch (e) { alert("Error saving line item: " + e.message); }
    finally { setSaving(false); }
  }

  async function remove(id) {
    if (!window.confirm("Remove this line item from the estimate?")) return;
    await sqlRead(`EXEC sp_SaveEstimateCostLineItem @payload = '${payloadOf({ line_item_id: id, revision_id: activeRevId, is_delete: true })}'`);
    onReload();
  }

  const total = lineItems.reduce((sum, li) => sum + (costFor(li)?.sellValue || 0), 0);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>Estimate Line Items</div>
        {!isLocked && <button onClick={() => setDraft({ ...emptyDraft })} style={{ fontSize: 12 }}>+ Add to Estimate</button>}
      </div>

      {draft && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8, padding: 12, border: "1px solid var(--cs-border)", borderRadius: 8, marginBottom: 12, background: "var(--surface)" }}>
          <select value={draft.Kind} onChange={e => setDraft({ ...draft, Kind: e.target.value, ItemID: null, AssemblyID: null })} style={inputSm}>
            <option value="assembly">Assembly</option>
            <option value="item">Item</option>
          </select>
          {draft.Kind === "assembly" ? (
            <select value={draft.AssemblyID || ""} onChange={e => setDraft({ ...draft, AssemblyID: +e.target.value })} style={{ ...inputSm, gridColumn: "span 2" }}>
              <option value="">Select an assembly —</option>
              {assemblies.map(a => <option key={a.AssemblyID} value={a.AssemblyID}>{a.Code} — {a.Description}</option>)}
            </select>
          ) : (
            <select value={draft.ItemID || ""} onChange={e => setDraft({ ...draft, ItemID: +e.target.value })} style={{ ...inputSm, gridColumn: "span 2" }}>
              <option value="">Select an item —</option>
              {items.map(i => <option key={i.ItemID} value={i.ItemID}>{i.Code ? `${i.Code} — ` : ""}{i.ItemName}</option>)}
            </select>
          )}
          <input type="number" step="0.01" placeholder="Qty" value={draft.Qty} onChange={e => setDraft({ ...draft, Qty: e.target.value })} style={inputSm} />
          <input placeholder="Description override (optional)" value={draft.Description} onChange={e => setDraft({ ...draft, Description: e.target.value })} style={{ ...inputSm, gridColumn: "span 2" }} />
          <select value={draft.LaborFactorID || ""} onChange={e => setDraft({ ...draft, LaborFactorID: e.target.value ? +e.target.value : null })} style={inputSm}>
            <option value="">Labor factor (default) —</option>
            {laborFactors.map(f => <option key={f.FactorID} value={f.FactorID}>{f.Code}</option>)}
          </select>
          <input placeholder="Notes" value={draft.Notes} onChange={e => setDraft({ ...draft, Notes: e.target.value })} style={inputSm} />
          <div style={{ gridColumn: "span 4", display: "flex", gap: 8 }}>
            <button
              onClick={save} disabled={saving || (draft.Kind === "assembly" ? !draft.AssemblyID : !draft.ItemID)}
              style={{ fontSize: 12, fontWeight: 600, color: "var(--cs-blue)" }}
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button onClick={() => setDraft(null)} style={{ fontSize: 12 }}>Cancel</button>
          </div>
        </div>
      )}

      <div style={{ overflowX: "auto", border: "1px solid var(--cs-border)", borderRadius: 8 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={th}>Category</th><th style={th}>Description</th><th style={th}>UOM</th><th style={th}>Qty</th>
            <th style={th}>Direct Cost</th><th style={th}>Sell Value</th><th style={th}></th>
          </tr></thead>
          <tbody>
            {lineItems.map(li => {
              const entity = entityFor(li);
              const cost = costFor(li);
              return (
                <tr key={li.LineItemID}>
                  <td style={td}>{categoryName(entity?.CategoryID)}</td>
                  <td style={td}>{li.Description || entity?.Description || entity?.ItemName || "—"}</td>
                  <td style={td}>{entity?.UOM || "—"}</td>
                  <td style={td}>{li.Qty}</td>
                  <td style={td}>{fmt(cost?.directCost)}</td>
                  <td style={td}>{fmt(cost?.sellValue)}</td>
                  <td style={td}>
                    {!isLocked && <>
                      <span onClick={() => setDraft({ ...li })} style={{ cursor: "pointer", color: "var(--cs-blue)", marginRight: 8 }}>Edit</span>
                      <span onClick={() => remove(li.LineItemID)} style={{ cursor: "pointer", color: "var(--text3)" }}>Remove</span>
                    </>}
                  </td>
                </tr>
              );
            })}
            {!lineItems.length && <tr><td style={td} colSpan={7}>No direct line items yet — use this for scope that isn't a circuit (switchgear, testing/commissioning, temporary power, general conditions).</td></tr>}
          </tbody>
          {!!lineItems.length && (
            <tfoot><tr style={{ fontWeight: 700 }}><td style={td} colSpan={5}>Total</td><td style={td}>{fmt(total)}</td><td style={td}></td></tr></tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
